import {
    MembershipItem,
    MembershipItemType,
    MEMBERSHIP_ITEM_TYPES,
    MembershipItemTypeValue
} from '@spike/membership-model';
import AddOnService from '@spike/addon-service-model';
import { Product } from '@spike/product-model';
import Service, { SERVICES_BUSINESS_AREAS } from 'model/Service';
import { v4 as uuid } from 'uuid';

/**
 * Resolves the granular itemType for a MembershipItem by looking up its itemID in the
 * provided service catalogs. This is necessary because the backend only stores a generic
 * 'Service' type for all service categories. When loading existing memberships, all
 * service items come back as MembershipItemType.SERVICE ("Primary Service") regardless
 * of whether they are grooming, daycare, boarding, etc.
 *
 * Items created via the new drawer flow already have granular types and are returned as-is.
 */
export const resolveItemTypeFromCatalogs = (
    item: MembershipItem,
    catalogs: Partial<Record<MembershipItemTypeValue, Service[]>>
): MembershipItemTypeValue => {
    const granularTypes = Object.values(MEMBERSHIP_ITEM_TYPES) as MembershipItemTypeValue[];

    // Already has a granular type from the new flow — keep it
    if (granularTypes.includes(item.itemType as MembershipItemTypeValue)) {
        return item.itemType as MembershipItemTypeValue;
    }

    // Legacy "Primary Service" → find in catalogs by itemID
    // String() on both sides guards against runtime number/string mismatches from backend JSON
    if (item.itemType === MembershipItemType.SERVICE) {
        for (const [type, services] of Object.entries(catalogs)) {
            if (services?.some(s => String(s.id) === String(item.itemID))) {
                return type as MembershipItemTypeValue;
            }
        }
        // Fallback: stays as the legacy service type — will be treated as generic service
        return item.itemType as MembershipItemTypeValue;
    }

    // Legacy product/add-on types map directly to their granular equivalents
    if (item.itemType === MembershipItemType.PRODUCT) return MEMBERSHIP_ITEM_TYPES.PRODUCT;
    if (item.itemType === MembershipItemType.ADD_ON) return MEMBERSHIP_ITEM_TYPES.ADD_ON;

    return item.itemType as MembershipItemTypeValue;
};

/**
 * Extracts the itemID and itemPrice from a catalog item (Service, AddOnService, or Product).
 * Mirrors the logic in MembershipItemsTableItem.changeItemSelectorHandler.
 */
export const extractItemDetails = (value: Service | AddOnService | Product): { itemID: string; itemPrice: number } => {
    let itemPrice = 0;
    let itemID = String(value.id);

    if ('pricing' in value) {
        const service = value as Service;
        if (service.businessArea?.id === SERVICES_BUSINESS_AREAS.TRANSPORTATION) {
            const fp = service.pricing.fixedPrice?.[0];
            itemPrice = fp?.transportationPrice?.pickUpPrice || 0;
        } else if (service.pricing.fixedPrice?.length) {
            itemPrice = service.pricing.fixedPrice[0].price || 0;
        }
        if (service.pricing.variablePrice?.prices?.length) {
            itemPrice = service.pricing.variablePrice.prices[0].price || 0;
        }
    }

    if ('price' in value && !('pricing' in value)) {
        itemPrice = Number((value as AddOnService).price);
    }

    if ('businessProduct' in value) {
        const product = value as Product;
        if (product.businessProduct) {
            itemID = String(product.businessProduct.id);
            itemPrice = Number(product.businessProduct.pricing?.price || 0);
        }
    }

    return { itemID, itemPrice };
};

/**
 * Converts a catalog item to a MembershipItem ready to be added to the table.
 * For discounted items, sets unlimited=true and initializes discount fields as null.
 */
export const toMembershipItem = (
    value: Service | AddOnService | Product,
    itemType: MembershipItemTypeValue,
    marketplaceID: number,
    forDiscounted = false
): MembershipItem => {
    const { itemID, itemPrice } = extractItemDetails(value);

    const item: MembershipItem = {
        id: 0,
        itemName: value.name,
        marketplaceID,
        membershipID: 0,
        itemID,
        itemType,
        uuid: uuid(),
        quantity: 1,
        amount: itemPrice,
        unlimited: forDiscounted,
        total: itemPrice.toFixed(2)
    };

    if (forDiscounted) {
        item.discountAmount = null;
        item.discountPercentage = null;
    }

    return item;
};
